import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, StatCard, StatRow, ToggleGroup, cardStyle, sectionTitleStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, orangeText } from './shared';

export default function UsagePage() {
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.getUsageSummary>> | null>(null);
  const [byPlan, setByPlan] = useState<Awaited<ReturnType<typeof api.getUsageByPlan>> | null>(null);
  const [limitHits, setLimitHits] = useState<Awaited<ReturnType<typeof api.getLimitHits>> | null>(null);
  const [tools, setTools] = useState<Awaited<ReturnType<typeof api.getPopularTools>> | null>(null);
  const [workflows, setWorkflows] = useState<Awaited<ReturnType<typeof api.getWorkflowStats>> | null>(null);
  const [range, setRange] = useState('30D');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, bp, lh, t, w] = await Promise.all([
        api.getUsageSummary(), api.getUsageByPlan(), api.getLimitHits(),
        api.getPopularTools(), api.getWorkflowStats(),
      ]);
      setSummary(s); setByPlan(bp); setLimitHits(lh); setTools(t); setWorkflows(w);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !summary) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <PageHeader title="Usage" subtitle={`Platform-wide usage metrics · ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}>
        <ToggleGroup options={['7D', '30D', '90D']} value={range} onChange={setRange} />
      </PageHeader>

      <StatRow>
        <StatCard label="AI CONVERSATIONS" value={summary?.aiConversations.toLocaleString() ?? '0'} sub={`${Math.round((summary?.aiConversations ?? 0) / 30)} avg/day · ${((summary?.aiConversations ?? 0) / Math.max(summary?.fileOperations ?? 1, 1) * 6.7).toFixed(1)} per active user`} />
        <StatCard label="FILE OPERATIONS" value={summary?.fileOperations.toLocaleString() ?? '0'} sub="Uploads, imports, drafts" />
        <StatCard label="E-SIGN DOCUMENTS" value={summary?.esignDocuments.toLocaleString() ?? '0'} sub="78% completion rate" />
        <StatCard label="API COST" value={summary?.apiCost != null ? `$${summary.apiCost}` : '—'} sub="$0.04 avg per request" />
      </StatRow>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {/* Usage by Plan */}
        <div style={{ ...cardStyle, flex: 2 }}>
          <span style={sectionTitleStyle}>Usage by Plan</span>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Plan</th>
                <th style={tableHeaderStyle}>AI Conv</th>
                <th style={tableHeaderStyle}>File Ops</th>
                <th style={tableHeaderStyle}>E-Sign</th>
                <th style={tableHeaderStyle}>Limit</th>
              </tr>
            </thead>
            <tbody>
              {byPlan?.map((row) => (
                <tr key={row.plan}>
                  <td style={{ ...tableCellStyle, fontWeight: 500, textTransform: 'capitalize', color: row.plan === 'pro' ? '#D46A3A' : row.plan === 'enterprise' ? '#7C3AED' : '#1A1714' }}>{row.plan}</td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{row.avgAi} avg</span>
                      <div style={{ width: 60, height: 5, backgroundColor: '#F0EBE4', borderRadius: 3 }}>
                        <div style={{ height: '100%', width: `${Math.min((row.avgAi / Math.max(row.limits?.aiConversations ?? row.avgAi, 1)) * 100, 100)}%`, backgroundColor: '#D46A3A', borderRadius: 3 }} />
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{row.avgFiles} avg</span>
                      <div style={{ width: 60, height: 5, backgroundColor: '#F0EBE4', borderRadius: 3 }}>
                        <div style={{ height: '100%', width: `${Math.min((row.avgFiles / Math.max(row.limits?.fileOperations ?? row.avgFiles, 1)) * 100, 100)}%`, backgroundColor: '#D46A3A', borderRadius: 3 }} />
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>{row.avgEsign} avg</td>
                  <td style={{ ...tableCellStyle, color: row.limits.aiConversations === null ? '#22883F' : '#8B7D6B', fontWeight: 500 }}>
                    {row.limits.aiConversations === null ? 'Unlimited' : `${row.limits.aiConversations} / ${row.limits.fileOperations} / ${row.limits.esignDocuments}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Limit Hits */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={sectionTitleStyle}>Limit Hits</span>
            <span style={{ ...orangeText, fontSize: 14 }}>{limitHits?.total ?? 0} this month</span>
          </div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <LimitCard label="AI Conversations" count={limitHits?.ai ?? 0} sub="free users hit 7/mo cap" />
            <LimitCard label="File Operations" count={limitHits?.files ?? 0} sub="free users hit 20/mo cap" />
            <LimitCard label="E-Sign" count={limitHits?.esign ?? 0} sub="free users hit 5/mo cap" />
          </div>
          {limitHits && limitHits.upgradeConversions != null && (
            <div style={{ marginTop: 16, backgroundColor: '#F0E8DF', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#8B5E3C', fontWeight: 500 }}>
              {limitHits.upgradeConversions} limit-hit users upgraded to Pro this month ({limitHits.total > 0 ? ((limitHits.upgradeConversions / limitHits.total) * 100).toFixed(1) : '0'}% conversion)
            </div>
          )}
        </div>
      </div>

      {/* Popular Tools + Workflow Runs */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Popular Tools</span>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
            {tools?.map((t, i) => (
              <div key={t.tool} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < (tools?.length ?? 0) - 1 ? '1px solid #F0EBE4' : 'none' }}>
                <span style={{ fontSize: 14, color: '#1A1714' }}>{t.tool}</span>
                <span style={{ fontSize: 14, color: '#D46A3A', fontWeight: 500 }}>{t.count.toLocaleString()} uses</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Workflow Runs</span>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <InfoRow label="Total runs" value={workflows?.total.toLocaleString() ?? '0'} />
            <InfoRow label="Success rate" value={workflows?.total ? `${((workflows.completed / workflows.total) * 100).toFixed(1)}%` : '—'} valueColor="#22883F" />
            <InfoRow label="Avg duration" value={workflows?.avgDuration ? `${(workflows.avgDuration / 1000).toFixed(1)}s` : '—'} />
            <InfoRow label="Failed runs" value={String(workflows?.failed ?? 0)} valueColor="#D44A3A" />
            <InfoRow label="Unique workflows" value={String(workflows?.uniqueWorkflows ?? '—')} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LimitCard({ label, count, sub }: { label: string; count: number; sub: string }) {
  return (
    <div style={{ backgroundColor: '#FAF7F4', borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#D46A3A' }}>{count}</span>
      </div>
      <span style={{ fontSize: 12, color: '#8B7D6B' }}>{count} {sub}</span>
    </div>
  );
}

function InfoRow({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 14, color: '#8B7D6B' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: valueColor ?? '#1A1714' }}>{value}</span>
    </div>
  );
}
